// Per-language Base64 encode/decode code examples used by the language how-to
// pages. Snippets are idiomatic and UTF-8 safe.

export interface LanguageGuide {
  slug: string;        // route slug, e.g. "javascript"
  name: string;        // display name, e.g. "JavaScript"
  label: string;       // CodeBlock label
  blurb: string;       // one-paragraph intro
  encode: string;
  decode: string;
  notes: string;       // extra prose about gotchas in this language
}

export const LANGUAGE_GUIDES: LanguageGuide[] = [
  {
    slug: 'javascript',
    name: 'JavaScript',
    label: 'JavaScript',
    blurb:
      'In the browser, JavaScript uses the built-in btoa() and atob() functions; in Node.js you use the Buffer class. Wrap btoa/atob for full UTF-8 support.',
    encode: `// Browser (UTF-8 safe)
const encoded = btoa(unescape(encodeURIComponent("Hello, World!")));

// Node.js
const encoded = Buffer.from("Hello, World!", "utf-8").toString("base64");`,
    decode: `// Browser (UTF-8 safe)
const text = decodeURIComponent(escape(atob(encoded)));

// Node.js
const text = Buffer.from(encoded, "base64").toString("utf-8");`,
    notes:
      'btoa() and atob() only handle Latin-1 directly, so the encodeURIComponent/unescape wrapper is required for emoji and non-ASCII text. In Node.js, Buffer handles UTF-8 natively.',
  },
  {
    slug: 'python',
    name: 'Python',
    label: 'Python',
    blurb:
      'Python ships with the base64 module. Encode bytes (not str), so encode your text to UTF-8 first, then decode the result back to a string.',
    encode: `import base64

encoded = base64.b64encode("Hello, World!".encode("utf-8")).decode("ascii")`,
    decode: `import base64

text = base64.b64decode(encoded).decode("utf-8")`,
    notes:
      'base64.b64encode takes and returns bytes, so call .encode("utf-8") on the input and .decode("ascii") on the output. For URL-safe Base64, use base64.urlsafe_b64encode / urlsafe_b64decode.',
  },
  {
    slug: 'java',
    name: 'Java',
    label: 'Java',
    blurb:
      'Since Java 8, java.util.Base64 provides standard, URL-safe and MIME encoders/decoders. Always pass an explicit charset when converting between String and byte[].',
    encode: `import java.util.Base64;
import java.nio.charset.StandardCharsets;

String encoded = Base64.getEncoder()
    .encodeToString("Hello, World!".getBytes(StandardCharsets.UTF_8));`,
    decode: `import java.util.Base64;
import java.nio.charset.StandardCharsets;

byte[] decoded = Base64.getDecoder().decode(encoded);
String text = new String(decoded, StandardCharsets.UTF_8);`,
    notes:
      'Use Base64.getUrlEncoder() for URL-safe output and Base64.getMimeEncoder() for MIME line-wrapped output. Specifying StandardCharsets.UTF_8 avoids platform-dependent encoding bugs.',
  },
  {
    slug: 'php',
    name: 'PHP',
    label: 'PHP',
    blurb:
      'PHP has built-in base64_encode() and base64_decode() functions that work directly on strings — no charset handling needed for UTF-8 text.',
    encode: `<?php
$encoded = base64_encode("Hello, World!");`,
    decode: `<?php
$text = base64_decode($encoded);`,
    notes:
      'base64_decode() returns false on failure when you pass true as the second (strict) argument. PHP has no built-in URL-safe variant, so use strtr($encoded, "+/", "-_") and trim the "=" padding yourself.',
  },
  {
    slug: 'csharp',
    name: 'C#',
    label: 'C#',
    blurb:
      'The .NET Convert class provides ToBase64String and FromBase64String, operating on byte arrays. Use Encoding.UTF8 to convert between string and bytes.',
    encode: `using System;
using System.Text;

string encoded = Convert.ToBase64String(Encoding.UTF8.GetBytes("Hello, World!"));`,
    decode: `using System;
using System.Text;

string text = Encoding.UTF8.GetString(Convert.FromBase64String(encoded));`,
    notes:
      'Convert.ToBase64String/FromBase64String use the standard alphabet only. For URL-safe output, replace "+"/"/" with "-"/"_" and strip padding yourself, or use Base64UrlEncoder from Microsoft.IdentityModel.Tokens.',
  },
  {
    slug: 'go',
    name: 'Go',
    label: 'Go',
    blurb:
      'Go\'s encoding/base64 package exposes ready-made encodings: StdEncoding for standard Base64 and URLEncoding for the URL-safe alphabet.',
    encode: `import "encoding/base64"

encoded := base64.StdEncoding.EncodeToString([]byte("Hello, World!"))`,
    decode: `import "encoding/base64"

decoded, err := base64.StdEncoding.DecodeString(encoded)
text := string(decoded)`,
    notes:
      'Go strings are already UTF-8 byte sequences, so no extra charset conversion is needed. Use base64.URLEncoding (or .WithPadding(base64.NoPadding)) for JWTs and URLs instead of StdEncoding.',
  },
  {
    slug: 'ruby',
    name: 'Ruby',
    label: 'Ruby',
    blurb:
      'Ruby\'s standard library base64 module provides strict_encode64/decode64, which handle Base64 without inserting line breaks.',
    encode: `require "base64"

encoded = Base64.strict_encode64("Hello, World!")`,
    decode: `require "base64"

text = Base64.strict_decode64(encoded)`,
    notes:
      'Avoid Base64.encode64 for anything except legacy MIME use — it inserts a newline every 60 characters. strict_encode64/strict_decode64 return a single unbroken line. For URL-safe output use Base64.urlsafe_encode64.',
  },
  {
    slug: 'rust',
    name: 'Rust',
    label: 'Rust',
    blurb:
      'Rust has no Base64 support in std; the base64 crate is the de facto standard, exposing explicit Engine types for standard and URL-safe alphabets.',
    encode: `use base64::{engine::general_purpose::STANDARD, Engine as _};

let encoded = STANDARD.encode("Hello, World!");`,
    decode: `use base64::{engine::general_purpose::STANDARD, Engine as _};

let bytes = STANDARD.decode(encoded)?;
let text = String::from_utf8(bytes)?;`,
    notes:
      'Since base64 crate v0.21, encoding/decoding goes through an explicit Engine (STANDARD, URL_SAFE, URL_SAFE_NO_PAD, etc.) rather than free functions, which the older 0.13 API used.',
  },
  {
    slug: 'swift',
    name: 'Swift',
    label: 'Swift',
    blurb:
      'Swift\'s Foundation Data type has built-in Base64 support: base64EncodedString() to encode and Data(base64Encoded:) to decode.',
    encode: `import Foundation

let encoded = Data("Hello, World!".utf8).base64EncodedString()`,
    decode: `import Foundation

if let data = Data(base64Encoded: encoded) {
    let text = String(data: data, encoding: .utf8)
}`,
    notes:
      'Data(base64Encoded:) returns an optional and fails (returns nil) on invalid input rather than throwing. Foundation has no built-in URL-safe variant — swap "+/" for "-_" and re-pad manually if you need one.',
  },
  {
    slug: 'kotlin',
    name: 'Kotlin',
    label: 'Kotlin',
    blurb:
      'On the JVM, Kotlin uses java.util.Base64 directly. Kotlin 1.8+ also ships an experimental kotlin.io.encoding.Base64 for multiplatform code.',
    encode: `import java.util.Base64

val encoded = Base64.getEncoder().encodeToString("Hello, World!".toByteArray(Charsets.UTF_8))`,
    decode: `import java.util.Base64

val decoded = Base64.getDecoder().decode(encoded)
val text = String(decoded, Charsets.UTF_8)`,
    notes:
      'Use Base64.getUrlEncoder() for the URL-safe alphabet. For Kotlin Multiplatform (non-JVM targets), use the experimental kotlin.io.encoding.Base64.Default / Base64.UrlSafe objects instead of java.util.Base64.',
  },
  {
    slug: 'cpp',
    name: 'C++',
    label: 'C++',
    blurb:
      'The C++ standard library has no Base64 support, so most projects either link OpenSSL/Boost or drop in a small lookup-table implementation.',
    encode: `#include <openssl/evp.h>

int len = EVP_EncodeBlock(reinterpret_cast<unsigned char*>(out),
                           reinterpret_cast<const unsigned char*>(in), in_len);`,
    decode: `#include <openssl/evp.h>

int len = EVP_DecodeBlock(reinterpret_cast<unsigned char*>(out),
                           reinterpret_cast<const unsigned char*>(in), in_len);`,
    notes:
      'EVP_EncodeBlock/EVP_DecodeBlock (from OpenSSL) are a common zero-dependency-beyond-OpenSSL choice; Boost.Beast and Boost.Archive also ship Base64 codecs. Note EVP_DecodeBlock does not strip trailing padding bytes from the output length automatically — you must account for "=" padding yourself.',
  },
  {
    slug: 'dart',
    name: 'Dart',
    label: 'Dart',
    blurb:
      'Dart\'s built-in dart:convert library provides base64Encode/base64Decode along with a base64Url codec for the URL-safe alphabet.',
    encode: `import 'dart:convert';

final encoded = base64Encode(utf8.encode("Hello, World!"));`,
    decode: `import 'dart:convert';

final text = utf8.decode(base64Decode(encoded));`,
    notes:
      'Use base64Url.encode/base64Url.decode for the URL-safe alphabet (used by Flutter apps for tokens and deep links). base64Decode throws a FormatException on malformed input, so wrap it in try/catch.',
  },
  {
    slug: 'bash',
    name: 'Bash',
    label: 'Bash',
    blurb:
      'Most Linux and macOS systems ship a base64 command-line tool that reads from stdin or a file and encodes or decodes standard Base64.',
    encode: `echo -n "Hello, World!" | base64`,
    decode: `echo "SGVsbG8sIFdvcmxkIQ==" | base64 --decode`,
    notes:
      'Always use echo -n (or printf) to avoid encoding a trailing newline. macOS\'s base64 uses -D instead of --decode. If base64 isn\'t available, openssl base64 and openssl base64 -d work as a drop-in alternative.',
  },
  {
    slug: 'sql',
    name: 'SQL',
    label: 'SQL',
    blurb:
      'Base64 functions differ by database: MySQL/MariaDB use TO_BASE64/FROM_BASE64, while PostgreSQL uses ENCODE/DECODE with an explicit "base64" format argument.',
    encode: `-- MySQL / MariaDB
SELECT TO_BASE64('Hello, World!');

-- PostgreSQL
SELECT ENCODE('Hello, World!'::bytea, 'base64');`,
    decode: `-- MySQL / MariaDB
SELECT FROM_BASE64(encoded);

-- PostgreSQL
SELECT DECODE(encoded, 'base64');`,
    notes:
      'MySQL\'s TO_BASE64 inserts a newline every 76 characters (MIME-style); wrap with REPLACE(..., \'\\n\', \'\') if you need a single line. PostgreSQL\'s DECODE returns bytea, so cast with ::text or convert_from() to get a readable string back.',
  },
];

export function getLanguageGuide(slug: string): LanguageGuide | undefined {
  return LANGUAGE_GUIDES.find((g) => g.slug === slug);
}
